import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Search, Briefcase, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import WhatsAppPill from "./WhatsAppPill";
import TelegramPill from "./TelegramPill";
import AlertCountPill from "./AlertCountPill";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/govt-jobs", label: "Govt Jobs" },
  { to: "/private-jobs", label: "Private Jobs" },
  { to: "/sectors", label: "Sectors" },
  { to: "/admit-cards", label: "Admit Card" },
  { to: "/results", label: "Result" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
];

// Free SEO tools — featured in header (desktop) + a dedicated footer column.
const TOOL_LINKS = [
  { to: "/tools/salary-calculator", label: "Salary Calculator" },
  { to: "/tools/rank-predictor", label: "Rank / Cut-off Predictor" },
  { to: "/schemes", label: "Govt Schemes" },
  { to: "/pyq", label: "PYQ Archive" },
  { to: "/guides", label: "Form-Fill Guides" },
];

const SECTOR_FOOTER_LINKS = [
  { to: "/sectors/railway", label: "Railway Jobs" },
  { to: "/sectors/banking", label: "Bank Jobs" },
  { to: "/sectors/ssc", label: "SSC Jobs" },
  { to: "/sectors/upsc", label: "UPSC Jobs" },
  { to: "/sectors/defense", label: "Defense Jobs" },
  { to: "/sectors/teaching", label: "Teaching Jobs" },
  { to: "/sectors/state-govt", label: "State Govt Jobs" },
  { to: "/sectors/psu", label: "PSU Jobs" },
];

const PRIVATE_EMPLOYER_FOOTER_LINKS = [
  { to: "/private-jobs/reliance-industries", label: "Reliance Industries" },
  { to: "/private-jobs/tata-consultancy-services", label: "TCS" },
  { to: "/private-jobs/infosys", label: "Infosys" },
  { to: "/private-jobs/hdfc-bank", label: "HDFC Bank" },
  { to: "/private-jobs/icici-bank", label: "ICICI Bank" },
  { to: "/private-jobs/tata-motors", label: "Tata Motors" },
  { to: "/private-jobs/bharti-airtel", label: "Bharti Airtel" },
  { to: "/private-jobs/zerodha", label: "Zerodha" },
  { to: "/private-jobs/dream11", label: "Dream11" },
  { to: "/private-jobs/nse-india", label: "NSE India" },
];

const FOOTER_LINKS = [
  { to: "/editorial-policy", label: "Editorial Policy" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/disclaimers", label: "Terms of Service & Disclaimers" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/all-india-jobs?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <div className="tricolor-bar" />
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 gap-4">
            <Link to="/" data-testid="logo-link" aria-label="hireds.in home" className="flex items-center gap-2 min-h-11">
              <div className="w-11 h-11 rounded-md bg-slate-900 text-white grid place-items-center">
                <Briefcase className="w-5 h-5 text-orange-500" aria-hidden="true" />
              </div>
              <div className="leading-tight">
                <div className="font-extrabold text-slate-900 text-lg tracking-tight" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>hireds<span className="text-orange-600">.in</span></div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sarkari Naukri Portal</div>
              </div>
            </Link>

            <form onSubmit={submitSearch} className="hidden lg:flex flex-1 max-w-md" data-testid="header-search-form" role="search">
              <div className="relative w-full">
                <label htmlFor="header-search" className="sr-only">Search jobs, exams, results</label>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
                <Input id="header-search" data-testid="header-search-input" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search jobs, exams, results..." aria-label="Search jobs, exams, results" className="pl-9 bg-slate-50" />
              </div>
            </form>

            <button data-testid="mobile-menu-toggle" aria-label={open?"Close menu":"Open menu"} aria-expanded={open} className="md:hidden min-w-11 min-h-11 p-2 text-slate-700 inline-flex items-center justify-center" onClick={()=>setOpen(!open)}>
              {open ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
            </button>

            <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
              {NAV.slice(0,6).map((n)=>(
                <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`} className={({isActive})=>`inline-flex items-center min-h-11 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive?'text-orange-600 bg-orange-50':'text-slate-700 hover:text-slate-900 hover:bg-slate-100'}`}>{n.label}</NavLink>
              ))}
              <div
                className="relative"
                onMouseEnter={()=>setToolsOpen(true)}
                onMouseLeave={()=>setToolsOpen(false)}
              >
                <button
                  type="button"
                  data-testid="nav-tools"
                  onClick={()=>setToolsOpen(v=>!v)}
                  aria-haspopup="true"
                  aria-expanded={toolsOpen}
                  className="inline-flex items-center gap-1 min-h-11 px-3 py-2 text-sm font-medium rounded-md text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                >
                  Tools <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
                {toolsOpen && (
                  <div
                    data-testid="nav-tools-menu"
                    className="absolute left-0 top-full mt-1 min-w-[230px] bg-white border border-slate-200 rounded-md shadow-lg py-2 z-50"
                  >
                    {TOOL_LINKS.map((t)=>(
                      <NavLink
                        key={t.to}
                        to={t.to}
                        onClick={()=>setToolsOpen(false)}
                        data-testid={`nav-tool-${t.to.split('/').pop()}`}
                        className={({isActive})=>`block px-4 py-2 text-sm ${isActive?'text-orange-600 bg-orange-50':'text-slate-700 hover:bg-slate-50'}`}
                      >
                        {t.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
              <WhatsAppPill variant="header" className="ml-2" testId="header-whatsapp-pill" />
              <TelegramPill variant="header" className="ml-1" testId="header-telegram-pill" />
            </nav>
          </div>

          {open && (
            <div className="md:hidden pb-4 space-y-1 border-t border-slate-200 pt-3">
              {NAV.map((n)=>(
                <NavLink key={n.to} to={n.to} data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g,'-')}`} onClick={()=>setOpen(false)} className={({isActive})=>`block min-h-11 px-3 py-3 text-sm font-medium rounded-md ${isActive?'bg-orange-50 text-orange-600':'text-slate-700 hover:bg-slate-100'}`}>{n.label}</NavLink>
              ))}
              <div className="pt-2 mt-2 border-t border-slate-100">
                <p className="px-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Free Tools</p>
                {TOOL_LINKS.map((t)=>(
                  <NavLink key={t.to} to={t.to} data-testid={`mnav-tool-${t.to.split('/').pop()}`} onClick={()=>setOpen(false)} className={({isActive})=>`block min-h-11 px-3 py-3 text-sm font-medium rounded-md ${isActive?'bg-orange-50 text-orange-600':'text-slate-700 hover:bg-slate-100'}`}>{t.label}</NavLink>
                ))}
              </div>
              <form onSubmit={submitSearch} className="flex gap-2 pt-2">
                <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search..." aria-label="Search jobs, exams, results" className="bg-slate-50 min-h-11" />
                <Button type="submit" className="min-h-11 bg-orange-600 hover:bg-orange-700 text-white">Go</Button>
              </form>
              <div className="pt-3 space-y-2">
                <WhatsAppPill variant="mobile" testId="mobile-whatsapp-pill" />
                <TelegramPill variant="mobile" testId="mobile-telegram-pill" />
              </div>
            </div>
          )}
        </div>
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-slate-900 text-slate-300 mt-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 grid md:grid-cols-3 lg:grid-cols-6 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-md bg-orange-600 grid place-items-center"><Briefcase className="w-4 h-4 text-white"/></div>
              <div className="font-extrabold text-white text-lg" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>hireds.in</div>
            </div>
            <p className="text-sm text-slate-200 leading-relaxed">India's trusted Sarkari Naukri portal — latest govt vacancy notices, admit cards & results, refreshed daily.</p>
            <address className="not-italic mt-4 text-xs text-slate-200 leading-relaxed" aria-label="Editorial contact">
              <strong className="text-white">hireds.in Editorial</strong><br/>
              Pune, Maharashtra, India<br/>
              <a href="mailto:editor@hireds.in" className="text-slate-100 hover:text-orange-400 underline">editor@hireds.in</a>
            </address>
            <div className="mt-4 flex gap-3" aria-label="Social media profiles">
              <a href="https://x.com/Hiredsdotin" target="_blank" rel="noopener noreferrer" aria-label="hireds.in on Twitter" title="Follow hireds.in on Twitter" className="w-11 h-11 rounded-md bg-slate-800 hover:bg-orange-600 grid place-items-center transition-colors">
                <span className="sr-only">Follow hireds.in on Twitter</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-white" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61589623707832" target="_blank" rel="noopener noreferrer" aria-label="hireds.in on Facebook" title="Follow hireds.in on Facebook" className="w-11 h-11 rounded-md bg-slate-800 hover:bg-orange-600 grid place-items-center transition-colors">
                <span className="sr-only">Follow hireds.in on Facebook</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-white" aria-hidden="true"><path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"/></svg>
              </a>
              <a href="https://t.me/hiredsdotin" target="_blank" rel="noopener noreferrer" aria-label="hireds.in on Telegram" title="Follow hireds.in on Telegram" className="w-11 h-11 rounded-md bg-slate-800 hover:bg-orange-600 grid place-items-center transition-colors">
                <span className="sr-only">Follow hireds.in on Telegram</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-white" aria-hidden="true"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
              </a>
              <a href="https://www.linkedin.com/in/hireds-dotin-55620a40a/" target="_blank" rel="noopener noreferrer" aria-label="hireds.in on LinkedIn" title="Follow hireds.in on LinkedIn" className="w-11 h-11 rounded-md bg-slate-800 hover:bg-orange-600 grid place-items-center transition-colors">
                <span className="sr-only">Follow hireds.in on LinkedIn</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-white" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">Explore</div>
            <ul className="space-y-1 text-sm">
              {NAV.slice(1,6).map((n)=>(<li key={n.to}><Link to={n.to} className="block py-2 text-slate-300 hover:text-orange-400">{n.label}</Link></li>))}
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">Sectors</div>
            <ul className="space-y-1 text-sm">
              {SECTOR_FOOTER_LINKS.map((n)=>(<li key={n.to}><Link to={n.to} data-testid={`footer-sector-${n.to.split('/').pop()}`} className="block py-2 text-slate-300 hover:text-orange-400">{n.label}</Link></li>))}
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">Top Employers</div>
            <ul className="space-y-1 text-sm">
              {PRIVATE_EMPLOYER_FOOTER_LINKS.map((n)=>(<li key={n.to}><Link to={n.to} data-testid={`footer-employer-${n.to.split('/').pop()}`} className="block py-2 text-slate-300 hover:text-orange-400">{n.label}</Link></li>))}
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">Free Tools</div>
            <ul className="space-y-1 text-sm">
              {TOOL_LINKS.map((t)=>(<li key={t.to}><Link to={t.to} data-testid={`footer-tool-${t.to.split('/').pop()}`} className="block py-2 text-slate-300 hover:text-orange-400">{t.label}</Link></li>))}
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">Company</div>
            <ul className="space-y-1 text-sm">
              <li><Link to="/about" className="block py-2 text-slate-300 hover:text-orange-400">About Us</Link></li>
              <li><Link to="/contact" className="block py-2 text-slate-300 hover:text-orange-400">Contact Us</Link></li>
              {FOOTER_LINKS.map((n)=>(<li key={n.to}><Link to={n.to} className="block py-2 text-slate-300 hover:text-orange-400">{n.label}</Link></li>))}
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">Admin</div>
            <ul className="space-y-1 text-sm">
              <li><Link to="/admin/login" data-testid="footer-admin-login" className="block py-2 text-slate-300 hover:text-orange-400">Admin Login</Link></li>
            </ul>
          </div>
        </div>

        {/* Pillar guides — hardcoded crawl paths to high-value evergreen content */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
          <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">Top Resources &amp; Guides</div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-sm" data-testid="footer-pillar-links">
            <li><Link to="/blog/ssc-cgl-2026-whats-actually-different-this-year-and-what-aspirants-are-getting-w-2e14af" className="block py-2 text-slate-300 hover:text-orange-400">SSC CGL 2026 — Pattern Shift &amp; What Aspirants Get Wrong</Link></li>
            <li><Link to="/blog/rrb-group-d-2026-complete-prep-guide" className="block py-2 text-slate-300 hover:text-orange-400">RRB Group D 2026 — Physical, Written &amp; Documents Guide</Link></li>
            <li><Link to="/blog/sbi-po-vs-ibps-po-which-is-better-2026" className="block py-2 text-slate-300 hover:text-orange-400">SBI PO vs IBPS PO 2026 — Which Should You Target First?</Link></li>
            <li><Link to="/blog/bank-po-salary-hike-2026-how-much-more-will-you-actually-take-home-583bf5" className="block py-2 text-slate-300 hover:text-orange-400">Bank PO Salary Hike 2026 — Real Take-Home Numbers</Link></li>
            <li><Link to="/blog/union-budget-2026-what-it-actually-means-for-the-sarkari-naukri-aspirant-f310f2" className="block py-2 text-slate-300 hover:text-orange-400">Union Budget 2026 — Sarkari Naukri Aspirant Impact</Link></li>
          </ul>
        </div>

        {/* Disclaimer — hireds.in is a private portal, not affiliated with any government body */}
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
            <div
              data-testid="footer-disclaimer"
              className="text-xs leading-relaxed text-amber-200 bg-slate-800/60 border border-slate-700 rounded-md px-4 py-3"
            >
              <strong className="text-white">Disclaimer:</strong> hireds.in is a private website and is not associated with or affiliated to any government organization or department. We gather information from official government recruitment notifications and websites solely for informational purposes.
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 text-xs text-slate-200 flex flex-col md:flex-row justify-between gap-2">
            <div>© {new Date().getFullYear()} hireds.in. All rights reserved. Pune, India.</div>
            <div>Made for Indian aspirants · Refreshed daily · SSL secured</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
